import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ChangeRequestFilter } from '../../../features/changeRequests/ChangeRequestFilter';
import CustomTable from '../../../components/common/CustomTable';
import { allCRcolumns } from '../columns';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    setFilters,
} from '../../../store/slices/tableController';
import { useSnackbar } from 'notistack';
import { useGetChangeRequestsMutation, useSearchChangeRequestsMutation } from '../../../api/changeRequests';

const AllRequests = ({ setCRStatus }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const controller = useSelector((state) => state.tableController);
    const dispatch = useDispatch();
    const [selectedCRs, setSelectedCRs] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState([]);

    const [searchChangeRequests, { isLoading: isSearchLoading }] = useSearchChangeRequestsMutation();
    const [getChangeRequests, { isLoading: isGetLoading }] = useGetChangeRequestsMutation();

    const [changeRequests, setChangeRequests] = useState({});
    const isLoading = isSearchLoading || isGetLoading;

    const fetchData = useCallback(async (searched, filters, sort, sortBy, page, pageSize) => {
        try {
            if (searched) {
                const query = new URLSearchParams({});
                if (filters.length) {
                    filters.forEach((filter) => query.append(filter.property, filter.value));
                }
                if (sortBy) {
                    query.append('sort', sortBy);
                    query.append('order', sort);
                }
                query.append('offset', page * pageSize);
                query.append('max', pageSize);

                const response = await searchChangeRequests({ query }).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setChangeRequests(response);
                response?.statusWiseStats && setCRStatus(response?.statusWiseStats);
            } else {
                const query = new URLSearchParams({});
                if (sortBy) {
                    query.append('sort', sortBy);
                    query.append('order', sort);
                }
                query.append('offset', page * pageSize);
                query.append('max', pageSize);

                const response = await getChangeRequests({ query }).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setChangeRequests(response);
                setCRStatus(response?.statusWiseStats);
            }
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
    }, []);

    useEffect(() => {
        fetchData(
            controller.searched,
            controller.filters,
            controller.sort,
            controller.sortBy,
            controller.page,
            controller.pageSize,
        );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };
    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleSelect = (event, rowId, row) => {
        const isChecked = event.target.checked;
        if (isChecked) {
            setSelectedCRs((prev) => [...prev, rowId]);
            if (row?.categoryType?.name === 'Major')
                setSelectedPosition((prev) => {
                    if (prev.filter((item) => item.id === row?.parentEntityId).length === 0)
                        return [
                            ...prev,
                            {
                                positionCode: row?.position?.positionCode,
                                id: row?.parentEntityId,
                                changeRequestId: row.id,
                            },
                        ];
                    else return prev;
                });
        } else {
            setSelectedCRs((prev) => prev.filter((id) => id !== rowId));
            if (row?.categoryType?.name === 'Major')
                setSelectedPosition((prev) => prev.filter((item) => item.id !== row?.parentEntityId));
        }
    };

    const handleSelectAll = (event) => {
        const isChecked = event.target.checked;
        if (isChecked) {
            let selectedCRs = [];
            let selectedPosition = [];
            changeRequests?.changeRequests.forEach((row) => {
                selectedCRs.push(row['id']);
                if (
                    selectedPosition.filter((item) => item.id === row?.position?.id).length === 0 &&
                    row?.categoryType?.name === 'Major'
                )
                    selectedPosition.push({
                        id: row?.position?.id,
                        positionCode: row?.position?.positionCode,
                        changeRequestId: row.id,
                    });
            });
            setSelectedPosition(selectedPosition);
            setSelectedCRs(selectedCRs);
        } else {
            setSelectedPosition([]);
            setSelectedCRs([]);
        }
    };

    return (
        <>
            <ChangeRequestFilter
                tab={'all'}
                disabled={false}
                filters={controller.filters}
                onFiltersApply={handleFiltersApply}
                onFiltersClear={handleFiltersClear}
                selectedCRs={selectedCRs}
                selectedPosition={selectedPosition}
                filterProperties={allCRcolumns}
                initialFilters={controller.initialFilters}
            />
            <CustomTable
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSelect={handleSelect}
                onSelectAll={handleSelectAll}
                onSortChange={handleSortChange}
                page={controller.page + 1}
                onRowClick={(row) => {
                    navigate(`/changeRequests/${row}?type=all`);
                }}
                columns={allCRcolumns}
                rows={changeRequests?.changeRequests || []}
                rowsCount={changeRequests?.changeRequestCount || 0}
                selectedRows={selectedCRs}
                sort={controller.sort}
                sortBy={controller.sortBy}
                showPagignation
                keyColumn="id"
                route="changerequests"
                action={() => {
                    setSelectedPosition([]);
                    setSelectedCRs([]);
                    fetchData(
                        controller.searched,
                        controller.filters,
                        controller.sort,
                        controller.sortBy,
                        controller.page,
                        controller.pageSize,
                    );
                }}
            />
        </>
    );
};

export default AllRequests;
